import React, { useCallback, useEffect, useState } from "react";
import BottomSheet from "./BottomSheet";
import { feedbackApi } from "../services/feedback";
import type { BoardIdea } from "../types/feedback";
import "./FeedbackBoardSheet.css";
import { formatLocale, t } from "../i18n";
import { T } from "../i18n/T";

// Feedback board (founder, 1.5.1 #10 — the simple version: "just have them
// upload, and a way for me to clear them"): talent post from "Share
// feedback / Feature request", see everyone's, upvote. Most upvoted first. No statuses to keep
// up — the founder clears ideas in Admin → Feedback. What Apple asks of
// anything one user posts for others to read (App Review 1.2) is covered by
// the server's word filter, Report on every idea, and the founder's Clear.

// "3 days ago" in the app's language.
function ago(iso: string): string {
  const then = new Date(iso).getTime();
  if (!isFinite(then)) return "";
  const rtf = new Intl.RelativeTimeFormat(formatLocale(), { numeric: "auto" });
  const mins = Math.round((then - Date.now()) / 60000);
  if (Math.abs(mins) < 60) return rtf.format(mins, "minute");
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return rtf.format(days, "day");
  const weeks = Math.round(days / 7);
  if (Math.abs(weeks) < 5) return rtf.format(weeks, "week");
  return rtf.format(Math.round(days / 30), "month");
}

const FeedbackBoardSheet: React.FC<{ open: boolean; onClose: () => void; onShareIdea: () => void }> = ({ open, onClose, onShareIdea }) => {
  const [ideas, setIdeas] = useState<BoardIdea[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [reported, setReported] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    setIdeas(null);
    setFailed(false);
    feedbackApi
      .board()
      .then((res) => setIdeas(Array.isArray(res?.ideas) ? res.ideas : []))
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Optimistic: the arrow answers instantly, the server's count wins after.
  const vote = (idea: BoardIdea) => {
    const flip = (i: BoardIdea, voted: boolean, votes: number) => (i.id === idea.id ? { ...i, voted, votes } : i);
    setIdeas((list) => list && list.map((i) => flip(i, !idea.voted, idea.votes + (idea.voted ? -1 : 1))));
    feedbackApi
      .vote(idea.id)
      .then((res) => setIdeas((list) => list && list.map((i) => flip(i, res.voted, res.votes))))
      .catch(() => setIdeas((list) => list && list.map((i) => flip(i, idea.voted, idea.votes))));
  };

  const report = (id: string) => {
    setReported((s) => new Set(s).add(id));
    feedbackApi.report(id).catch(() => {
      /* the founder reviews every idea anyway — a lost report is not a dead end */
    });
  };

  return (
    <BottomSheet open={open} title={t("feedbackBoard.feedbackBoard")} onClose={onClose} tall>
      <div className="fbb">
        <button type="button" className="fbb-share" onClick={onShareIdea}>
          {t("feedbackBoard.shareFeedbackOrRequest")}
        </button>

        {failed ? (
          <div className="fbb-empty">
            <T k="feedbackBoard.couldNotLoad" tags={{ 0: (c) => <button type="button" className="fbb-link" onClick={load}>{c}</button> }} />
          </div>
        ) : ideas === null ? (
          <div className="fbb-empty">{t("feedbackBoard.loading")}</div>
        ) : ideas.length === 0 ? (
          <div className="fbb-empty">{t("feedbackBoard.noPostsYet")}</div>
        ) : (
          <ul className="fbb-list">
            {ideas.map((idea) => {
              const expanded = openId === idea.id;
              return (
                <li key={idea.id} className={`fbb-idea${expanded ? " open" : ""}`}>
                  <button
                    type="button"
                    className={`fbb-vote${idea.voted ? " on" : ""}`}
                    aria-pressed={idea.voted}
                    aria-label={t("feedbackBoard.upvote", { count: idea.votes })}
                    onClick={() => vote(idea)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6l6.5 8h-13z" fill="currentColor" /></svg>
                    <span>{idea.votes.toLocaleString(formatLocale())}</span>
                  </button>
                  <button type="button" className="fbb-body" aria-expanded={expanded} onClick={() => setOpenId(expanded ? null : idea.id)}>
                    <span className="fbb-title">{idea.title}</span>
                    {expanded && idea.message && <span className="fbb-msg">{idea.message}</span>}
                    <span className="fbb-meta">
                      {t("feedbackBoard.byWhen", { name: idea.author, when: ago(idea.createdAt) })}
                    </span>
                  </button>
                  {expanded && (
                    <button type="button" className="fbb-report" disabled={reported.has(idea.id)} onClick={() => report(idea.id)}>
                      {reported.has(idea.id) ? t("feedbackBoard.reported") : t("feedbackBoard.report")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <p className="fbb-note">{t("feedbackBoard.weReadEveryPost")}</p>
      </div>
    </BottomSheet>
  );
};

export default FeedbackBoardSheet;
